import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner } from 'react-bootstrap';
import { ClipboardList } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ApiClient from '../utilities/http';
import { Endpoints } from '../utilities/endpoints';
import FancyButton from './utilities/FancyButton';
import '../assets/styles/asset-activity-status.css';

interface AssetActivityStatusItem {
    id: string;
    activity_uid: string;
    description: string | null;
    activity_type: string | null;
    status: string | null;
}

const AssetActivityStatus: React.FC<{ assetId: string; identifier: string }> = ({ assetId, identifier }) => {
    const { t: translate } = useTranslation('assetActivityStatus');
    const t = translate as (key: string) => string;
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [activities, setActivities] = useState<AssetActivityStatusItem[]>([]);

    useEffect(() => {
        if (!show) return;
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        setActivities([]);
        ApiClient.get<{ activities: AssetActivityStatusItem[] }>(
            `${Endpoints.ASSET}/${encodeURIComponent(assetId)}/activities`, { signal: controller.signal },
        ).then(({ data }) => {
            if (!controller.signal.aborted) setActivities(data.activities);
        }).catch(() => {
            if (!controller.signal.aborted) setError(true);
        }).finally(() => {
            if (!controller.signal.aborted) setLoading(false);
        });
        return () => controller.abort();
    }, [show, assetId]);

    return <>
        <FancyButton id="asset-status-btn" icon={ClipboardList} className="w-100"
            clickFunction={() => setShow(true)} label={t('button')} />
        <Modal show={show} onHide={() => setShow(false)} centered scrollable
            className="asset-activity-status" aria-labelledby="asset-activity-status-title">
            <Modal.Header closeButton>
                <div className="asset-activity-status-heading">
                    <Modal.Title id="asset-activity-status-title">{t('title')}</Modal.Title>
                    <p className="asset-activity-status-identifier">{identifier}</p>
                </div>
            </Modal.Header>
            <Modal.Body>
                {loading ? <div className="asset-activity-status-message" role="status"><Spinner size="sm" aria-hidden="true" /> {t('loading')}</div>
                    : error ? <Alert variant="danger">{t('error')}</Alert>
                    : activities.length === 0 ? <p className="asset-activity-status-message">{t('empty')}</p>
                    : <ul className="asset-activity-status-list">
                        {activities.map(activity => <li className="asset-activity-status-item" key={activity.id}>
                            <div className="asset-activity-status-item-header">
                                <strong className="asset-activity-status-uid">{activity.activity_uid}</strong>
                                <Badge bg="light" text="dark" className="asset-activity-status-badge">{activity.status || t('unknown_status')}</Badge>
                            </div>
                            {activity.activity_type && <p className="asset-activity-status-type">{activity.activity_type}</p>}
                            {activity.description && <p className="asset-activity-status-description">{activity.description}</p>}
                        </li>)}
                    </ul>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShow(false)}>{t('close')}</Button>
            </Modal.Footer>
        </Modal>
    </>;
};

export default AssetActivityStatus;
